// תשלומי לקוחות — how much of each order the customer has paid, and how much is still owed.
//
// Every payment is recorded separately (date + amount); the paid total is their sum.
// The order total comes live from the order lines (quantity × customer price), so it is
// never typed here. Orders are grouped by the year they were created (Israel time).

import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import api, { fmt } from '../../utils/api';
import { confirm } from '../../utils/confirm';

const STATUS_LABEL = {
  he: { pending: 'ממתין', ready: 'מוכן', picked: 'לוקט', shipped: 'נשלח', completed: 'הושלם', cancelled: 'בוטל' },
  en: { pending: 'Pending', ready: 'Ready', picked: 'Picked', shipped: 'Shipped', completed: 'Completed', cancelled: 'Cancelled' },
};

// English text for the server's error codes (the server's own `error` is Hebrew).
const ERROR_EN = {
  BAD_AMOUNT: () => 'Invalid amount — a positive number with up to 2 decimals',
  BAD_NOTE: () => 'Invalid note',
  NOTE_TOO_LONG: (p) => `The note is too long (up to ${p.max} characters)`,
  BAD_DATE: () => 'Invalid date',
  DATE_OUT_OF_RANGE: () => 'The date is out of range',
  FUTURE_DATE: () => 'A payment date cannot be in the future',
  ALREADY_PAID: (p) => `Order ${p.order} is already paid in full`,
  OVER_BALANCE: (p) => `The amount is above the balance due on order ${p.order}: ${fmt(p.remaining)} left`,
  BALANCE_CHANGED: (p) => `The balance of order ${p.order} changed since the page loaded — ${fmt(p.remaining)} is now due. Please confirm again.`,
  ORDER_NOT_FOUND: () => 'Order not found',
  PAYMENT_NOT_FOUND: () => 'Payment not found',
  BAD_ORDER_ID: () => 'Invalid order id',
  BAD_PAYMENT_ID: () => 'Invalid payment id',
};

const COLS = 11;
const MIN_DATE = '2020-01-01';
const MAX_EXPECTED_DATE = '2100-12-31';

const d2 = (n) => String(n).padStart(2, '0');
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d2(d.getMonth() + 1)}-${d2(d.getDate())}`;
};
const isIsoDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
const fmtDay = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
const cents = (v) => Math.round((Number(v) || 0) * 100);
const fmtPct = (p) => `${Number.isInteger(p) ? p : p.toFixed(1)}%`;
const ordersLabel = (n, he) => (he ? (n === 1 ? 'הזמנה אחת' : `${n} הזמנות`) : (n === 1 ? '1 order' : `${n} orders`));
const paymentsLabel = (n, he) => (he ? (n === 1 ? 'תשלום אחד' : `${n} תשלומים`) : (n === 1 ? '1 payment' : `${n} payments`));

// Percentages from integer cents, rounded toward the safe side: "paid" shows 100% only
// when fully paid, and anything still owed shows at least 0.1%.
const pctFloor = (part, whole) => (whole > 0 ? Math.min(100, Math.floor((part * 1000) / whole) / 10) : 0);
const pctCeil = (part, whole) => (whole > 0 ? Math.min(100, Math.ceil((part * 1000) / whole) / 10) : 0);

/** Everything the table shows for one order, derived from its total and paid sum. */
function balanceOf(order, today) {
  const totalC = cents(order.total);
  const paidC = cents(order.paid);
  const remainingC = totalC - paidC;
  // Settled: nothing left to collect. That includes an order whose total dropped (or
  // was cancelled) after it was paid — the payment is not "owed" again.
  const settled = remainingC <= 0 && (totalC > 0 || paidC > 0);
  const owedC = Math.max(0, remainingC);
  return {
    totalC,
    paidC,
    owedC,
    settled,
    open: owedC > 0,
    overpaidC: remainingC < 0 ? -remainingC : 0,
    pctPaid: settled ? 100 : pctFloor(paidC, totalC),
    pctDue: pctCeil(owedC, totalC),
    overdue: owedC > 0 && !!order.expected_payment_date && order.expected_payment_date < today,
  };
}

function sumBalances(rows) {
  const totalC = rows.reduce((s, r) => s + r.b.totalC, 0);
  const paidC = rows.reduce((s, r) => s + r.b.paidC, 0);
  const owedC = rows.reduce((s, r) => s + r.b.owedC, 0);
  return {
    totalC,
    paidC,
    owedC,
    pctPaid: owedC === 0 && totalC > 0 ? 100 : pctFloor(paidC, totalC),
    pctDue: pctCeil(owedC, totalC),
  };
}

// Excel cells: real numbers and dates with a display format, so the sheet can be summed
// and sorted. dd/mm/yyyy;@ keeps day-first on every Excel locale (plain dd/mm/yyyy is
// re-mapped by Excel to the viewer's short date). A missing value is null, which
// aoa_to_sheet leaves as a truly empty cell ('' would be an empty-text cell that breaks
// ISBLANK, COUNTA and date arithmetic).
const XL_MONEY = '"$"#,##0.00';
const XL_PCT = '0.0%';
const XL_DATE = 'dd/mm/yyyy;@';
const xlMoney = (c) => ({ t: 'n', v: c / 100, z: XL_MONEY });
const xlPct = (p) => ({ t: 'n', v: p / 100, z: XL_PCT });
const xlDate = (iso) => {
  if (!isIsoDate(iso)) return null;
  const serial = Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86400000 + 25569;
  return { t: 'n', v: serial, z: XL_DATE };
};
const xlOrderNo = (s) => (/^\d+$/.test(s || '') ? Number(s) : s || null);

function ProgressBar({ pct, done }) {
  return (
    <div style={{ height: 6, background: '#eef0f3', borderRadius: 3, overflow: 'hidden', marginTop: 4, minWidth: 60 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: done ? '#1a7a4a' : '#2563eb' }} />
    </div>
  );
}

/** The expected-payment date of one order. Typing only edits a draft; the date is saved
 *  when the field loses focus (or on Enter), so a half-typed date is never stored. */
function ExpectedDateInput({ order, overdue, he, onSave }) {
  const saved = order.expected_payment_date || '';
  const [draft, setDraft] = useState(saved);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(saved); }, [saved]);

  const commit = async () => {
    if (draft === saved) return;
    if (draft !== '' && !(isIsoDate(draft) && draft >= MIN_DATE && draft <= MAX_EXPECTED_DATE)) {
      setDraft(saved);
      onSave(null, he ? 'תאריך צפוי לא תקין — יש להזין תאריך מלא בין 2020 ל-2100' : 'Invalid expected date — enter a full date between 2020 and 2100');
      return;
    }
    setSaving(true);
    const ok = await onSave(draft);
    setSaving(false);
    if (!ok) setDraft(saved);
  };

  return (
    <>
      <input className="inline-edit" type="date" min={MIN_DATE} max={MAX_EXPECTED_DATE}
        style={{ width: 122, borderColor: overdue ? '#dc2626' : undefined, opacity: saving ? 0.6 : 1 }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
      {overdue && (
        <div style={{ color: '#b91c1c', fontSize: 11, fontWeight: 600 }}>{he ? 'עבר המועד' : 'Overdue'}</div>
      )}
    </>
  );
}

export default function CustomerPaymentsPage() {
  const { i18n } = useTranslation();
  const he = i18n.language === 'he';
  const today = todayISO();
  const emptyForm = { amount: '', paidOn: today, note: '' };

  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');          // all | open | paid
  const [expanded, setExpanded] = useState(null);       // order id with its payments panel open
  const [collapsedYears, setCollapsedYears] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const errorText = useCallback((e, failHe, failEn) => {
    const data = e?.response?.data || {};
    if (he) return data.error || failHe;
    const en = ERROR_EN[data.code];
    return en ? en(data.params || {}) : failEn;
  }, [he]);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/customer-payments');
      setOrders(r.data.orders || []);
      setPayments(r.data.payments || []);
      setErr('');
    } catch (e) {
      setErr(errorText(e, 'שגיאה בטעינת תשלומי לקוחות', 'Failed to load customer payments'));
    }
    setLoading(false);
  }, [errorText]);

  useEffect(() => { load(); }, [load]);

  const paymentsByOrder = useMemo(() => {
    const m = {};
    payments.forEach((p) => { (m[p.cellcom_order_id] = m[p.cellcom_order_id] || []).push(p); });
    return m;
  }, [payments]);

  const rows = useMemo(() => orders.map((o) => ({ o, b: balanceOf(o, today) })), [orders, today]);

  const years = useMemo(() => {
    const shown = rows.filter(({ b }) => (filter === 'open' ? b.open : filter === 'paid' ? b.settled : true));
    const byYear = {};
    shown.forEach((r) => { (byYear[r.o.year] = byYear[r.o.year] || []).push(r); });
    return Object.keys(byYear).map(Number).sort((a, b) => b - a).map((y) => ({ year: y, rows: byYear[y] }));
  }, [rows, filter]);

  const all = sumBalances(rows);
  const overdueRows = rows.filter((r) => r.b.overdue);
  const overdueC = overdueRows.reduce((s, r) => s + r.b.owedC, 0);

  const toggleOrder = (id) => {
    setExpanded((cur) => (cur === id ? null : id));
    setForm(emptyForm);
    setErr('');
  };

  /** Runs one write, reloads the table, and reports success. On a 409 (the balance
   *  changed under the user) the table is reloaded too, so the new balance is visible. */
  const run = async (fn, failHe, failEn) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setErr(errorText(e, failHe, failEn));
      if (e?.response?.status === 409) await load();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const checkPaidOn = (paidOn) => {
    if (!isIsoDate(paidOn) || paidOn < MIN_DATE) return he ? 'יש לבחור תאריך תשלום תקין' : 'Choose a valid payment date';
    if (paidOn > today) return he ? 'תאריך תשלום לא יכול להיות בעתיד' : 'A payment date cannot be in the future';
    return '';
  };

  const addPayment = async (order, b) => {
    const raw = String(form.amount).trim();
    if (!/^\d+(\.\d{1,2})?$/.test(raw) || cents(raw) <= 0) {
      setErr(he ? 'יש להזין סכום חיובי עם עד 2 ספרות אחרי הנקודה' : 'Enter a positive amount with up to 2 decimals');
      return;
    }
    if (cents(raw) > b.owedC) {
      setErr(he ? `הסכום גבוה מהיתרה לתשלום (${fmt(b.owedC / 100)})` : `The amount is above the balance due (${fmt(b.owedC / 100)})`);
      return;
    }
    const dateErr = checkPaidOn(form.paidOn);
    if (dateErr) { setErr(dateErr); return; }
    const ok = await run(
      () => api.post(`/customer-payments/orders/${order.id}/payments`, { amount: Number(raw), paidOn: form.paidOn, note: form.note }),
      'שגיאה ברישום התשלום', 'Failed to record the payment'
    );
    if (ok) setForm(emptyForm);
  };

  /** Records the whole balance. The confirmed amount is sent along; the server refuses
   *  if the balance is no longer that amount. Returns true when recorded. */
  const payRemaining = async (order, b, paidOn, note) => {
    const dateErr = checkPaidOn(paidOn);
    if (dateErr) { setErr(dateErr); return false; }
    const body = he
      ? `לרשום תשלום של ${fmt(b.owedC / 100)} להזמנה ${order.order_number} בתאריך ${fmtDay(paidOn)}?\nההזמנה תסומן כשולמה במלואה.`
      : `Record a payment of ${fmt(b.owedC / 100)} for order ${order.order_number} on ${fmtDay(paidOn)}?\nThe order will be marked as paid in full.`;
    if (!(await confirm({ title: he ? 'סימון כשולם' : 'Mark as paid', body, confirmLabel: he ? 'רשום תשלום' : 'Record payment' }))) return false;
    return run(
      () => api.post(`/customer-payments/orders/${order.id}/pay-remaining`, { paidOn, note, expectedAmount: b.owedC / 100 }),
      'שגיאה ברישום התשלום', 'Failed to record the payment'
    );
  };

  const removePayment = async (order, p) => {
    const body = he
      ? `למחוק את התשלום של ${fmt(p.amount_usd)} מתאריך ${fmtDay(p.paid_on)} בהזמנה ${order.order_number}?`
      : `Delete the payment of ${fmt(p.amount_usd)} from ${fmtDay(p.paid_on)} on order ${order.order_number}?`;
    if (!(await confirm({ title: he ? 'מחיקת תשלום' : 'Delete payment', body, confirmLabel: he ? 'מחק' : 'Delete', danger: true }))) return;
    await run(() => api.delete(`/customer-payments/payments/${p.id}`), 'שגיאה במחיקת התשלום', 'Failed to delete the payment');
  };

  /** Saves one order's expected date. Called with (null, message) to only show an error. */
  const saveExpectedDate = async (order, value, message) => {
    if (value === null) { setErr(message); return false; }
    setErr('');
    try {
      await api.put(`/customer-payments/orders/${order.id}/expected-date`, { expectedDate: value || null });
      await load();
      return true;
    } catch (e) {
      setErr(errorText(e, 'שגיאה בשמירת התאריך הצפוי', 'Failed to save the expected date'));
      return false;
    }
  };

  const statusText = (s) => STATUS_LABEL[he ? 'he' : 'en'][s] || s || '—';
  const collapsedArrow = he ? '◂ ' : '▸ ';
  const moneyCell = { fontFamily: "'DM Mono', monospace", whiteSpace: 'nowrap' };

  /** Exports what the table shows (current filter, every year even if collapsed):
   *  sheet 1 = the orders with year and grand totals, sheet 2 = every payment of those orders.
   *  Overpayment has its own column so "balance due" always sums to its total row. */
  const exportExcel = () => {
    const filterLabel = {
      all: he ? 'הכל' : 'All',
      open: he ? 'פתוחות לתשלום' : 'Open',
      paid: he ? 'שולמו' : 'Paid',
    }[filter];
    const title = [
      [he ? 'תשלומי לקוחות' : 'Customer Payments'],
      [he ? 'תאריך ייצוא' : 'Exported', xlDate(today)],
      [he ? 'סינון' : 'Filter', filterLabel],
      [],
    ];

    const orderHead = he
      ? ['שנה', 'הזמנה', 'תאריך הזמנה', 'חשבונית', 'סטטוס', 'סכום ההזמנה', 'שולם', '% שולם', 'יתרה לתשלום', '% יתרה', 'שולם ביתר', 'תשלום צפוי', 'עבר המועד', 'מספר תשלומים', 'תשלום אחרון']
      : ['Year', 'Order', 'Order date', 'Invoice', 'Status', 'Order total', 'Paid', '% paid', 'Balance due', '% due', 'Overpaid', 'Expected payment', 'Overdue', 'Payments', 'Last payment'];
    const shownRows = years.flatMap(({ rows: yRows }) => yRows);
    const orderRows = shownRows.map(({ o, b }) => [
      o.year,
      xlOrderNo(o.order_number),
      xlDate(o.order_date),
      o.invoice_number || null,
      statusText(o.status),
      xlMoney(b.totalC),
      xlMoney(b.paidC),
      xlPct(b.pctPaid),
      xlMoney(b.owedC),
      xlPct(b.pctDue),
      b.overpaidC > 0 ? xlMoney(b.overpaidC) : null,
      b.open ? xlDate(o.expected_payment_date) : null,
      b.overdue ? (he ? 'כן' : 'Yes') : null,
      o.payment_count,
      xlDate(o.last_paid_on),
    ]);
    const totalRow = (label, rowsOfTotal) => {
      const s = sumBalances(rowsOfTotal);
      const overpaidC = rowsOfTotal.reduce((sum, r) => sum + r.b.overpaidC, 0);
      return [
        label, ordersLabel(rowsOfTotal.length, he), null, null, null,
        xlMoney(s.totalC), xlMoney(s.paidC), xlPct(s.pctPaid), xlMoney(s.owedC), xlPct(s.pctDue),
        overpaidC > 0 ? xlMoney(overpaidC) : null,
      ];
    };
    const totals = years.map(({ year, rows: yRows }) => totalRow(he ? `סה"כ ${year}` : `Total ${year}`, yRows));
    if (years.length > 1) totals.push(totalRow(he ? 'סה"כ כולל' : 'Grand total', shownRows));

    const wsOrders = XLSX.utils.aoa_to_sheet([...title, orderHead, ...orderRows, [], ...totals]);
    wsOrders['!cols'] = [8, 10, 12, 10, 10, 16, 16, 9, 16, 9, 14, 14, 10, 10, 13].map((wch) => ({ wch }));

    const payHead = he
      ? ['הזמנה', 'תאריך תשלום', 'סכום', 'הערה', 'נרשם ע"י']
      : ['Order', 'Payment date', 'Amount', 'Note', 'Recorded by'];
    const payRows = [];
    let payTotalC = 0;
    shownRows.forEach(({ o }) => (paymentsByOrder[o.id] || []).forEach((p) => {
      payTotalC += cents(p.amount_usd);
      payRows.push([xlOrderNo(o.order_number), xlDate(p.paid_on), xlMoney(cents(p.amount_usd)), p.note || null, p.created_by_name || null]);
    }));
    const payTotal = payRows.length
      ? [[], [he ? 'סה"כ' : 'Total', paymentsLabel(payRows.length, he), xlMoney(payTotalC)]]
      : [];
    const wsPayments = XLSX.utils.aoa_to_sheet([...title, payHead, ...payRows, ...payTotal]);
    wsPayments['!cols'] = [10, 13, 16, 40, 18].map((wch) => ({ wch }));

    const wb = XLSX.utils.book_new();
    wb.Workbook = { Views: [{ RTL: he }] };
    XLSX.utils.book_append_sheet(wb, wsOrders, he ? 'הזמנות' : 'Orders');
    XLSX.utils.book_append_sheet(wb, wsPayments, he ? 'תשלומים' : 'Payments');
    XLSX.writeFile(wb, `customer_payments_${fmtDay(today).replace(/\//g, '-')}.xlsx`);
  };

  const renderPanel = (o, b) => {
    const list = paymentsByOrder[o.id] || [];
    return (
      <tr>
        <td colSpan={COLS} style={{ background: '#f8fafc', padding: 16 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 380px', minWidth: 320 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                {he ? `תשלומים שהתקבלו — הזמנה ${o.order_number}` : `Payments received — order ${o.order_number}`}
              </div>
              <table className="table" style={{ fontSize: 13, margin: 0, background: '#fff' }}>
                <thead><tr>
                  <th>{he ? 'תאריך' : 'Date'}</th>
                  <th className="text-end">{he ? 'סכום' : 'Amount'}</th>
                  <th>{he ? 'הערה' : 'Note'}</th>
                  <th>{he ? 'נרשם ע"י' : 'Recorded by'}</th>
                  <th style={{ width: 40 }}></th>
                </tr></thead>
                <tbody>
                  {list.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 12 }}>
                      {he ? 'עדיין לא התקבל תשלום על הזמנה זו' : 'No payment received for this order yet'}
                    </td></tr>
                  )}
                  {list.map((p) => (
                    <tr key={p.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDay(p.paid_on)}</td>
                      <td className="text-end" style={{ ...moneyCell, fontWeight: 600 }}>{fmt(p.amount_usd)}</td>
                      <td style={{ color: '#475569', overflowWrap: 'anywhere', minWidth: 120 }}>{p.note || '—'}</td>
                      <td style={{ color: '#64748b', fontSize: 12 }}>{p.created_by_name || '—'}</td>
                      <td>
                        <button className="btn btn-sm btn-outline" style={{ color: '#b91c1c', borderColor: '#fecaca' }}
                          disabled={busy} title={he ? 'מחק תשלום' : 'Delete payment'}
                          onClick={() => removePayment(o, p)}>✕</button>
                      </td>
                    </tr>
                  ))}
                  {list.length > 0 && (
                    <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
                      <td>{he ? 'סה"כ שולם' : 'Total paid'}</td>
                      <td className="text-end" style={moneyCell}>{fmt(b.paidC / 100)}</td>
                      <td colSpan={3} style={{ color: '#64748b', fontWeight: 500 }}>
                        {he ? `מתוך ${fmt(b.totalC / 100)}` : `of ${fmt(b.totalC / 100)}`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ flex: '0 1 360px', minWidth: 300, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
              {b.overpaidC > 0 && (
                <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
                  ⚠ {he
                    ? `שולם ${fmt(b.overpaidC / 100)} יותר מסכום ההזמנה (סכום ההזמנה ירד או שההזמנה בוטלה אחרי התשלום)`
                    : `Paid ${fmt(b.overpaidC / 100)} more than the order total (the order was reduced or cancelled after payment)`}
                </div>
              )}
              {b.settled ? (
                <div style={{ color: '#1a7a4a', fontWeight: 700, fontSize: 14 }}>
                  ✓ {he ? 'ההזמנה שולמה במלואה' : 'This order is paid in full'}
                </div>
              ) : !b.open ? (
                <div style={{ color: '#64748b', fontSize: 13 }}>
                  {he ? 'אין בהזמנה שורות עם מחיר — אין סכום לתשלום' : 'The order has no priced lines — nothing to pay'}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
                    {he ? 'רישום תשלום' : 'Record a payment'}
                    <span style={{ color: '#c2410c', fontWeight: 600, marginInlineStart: 8 }}>
                      {he ? `יתרה לתשלום: ${fmt(b.owedC / 100)}` : `Balance due: ${fmt(b.owedC / 100)}`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                    <label style={{ fontSize: 12 }}>{he ? 'סכום שהתקבל ($)' : 'Amount received ($)'}<br />
                      <input className="inline-edit" type="number" min="0" step="0.01" style={{ width: 140 }}
                        value={form.amount} placeholder={(b.owedC / 100).toFixed(2)}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                    </label>
                    <label style={{ fontSize: 12 }}>{he ? 'תאריך התשלום' : 'Payment date'}<br />
                      <input className="inline-edit" type="date" style={{ width: 150 }} min={MIN_DATE} max={today}
                        value={form.paidOn} onChange={(e) => setForm({ ...form, paidOn: e.target.value })} />
                    </label>
                  </div>
                  <label style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>{he ? 'הערה (לא חובה)' : 'Note (optional)'}<br />
                    <input className="inline-edit" style={{ width: '100%' }} maxLength={500}
                      value={form.note} placeholder={he ? 'לדוגמה: העברה בנקאית' : 'e.g. bank transfer'}
                      onChange={(e) => setForm({ ...form, note: e.target.value })} />
                  </label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm btn-gold" disabled={busy || !form.paidOn || !(parseFloat(form.amount) > 0)}
                      onClick={() => addPayment(o, b)}>
                      ➕ {he ? 'הוסף תשלום' : 'Add payment'}
                    </button>
                    <button className="btn btn-sm btn-success" disabled={busy || !form.paidOn}
                      onClick={async () => { if (await payRemaining(o, b, form.paidOn, form.note)) setForm(emptyForm); }}>
                      ✓ {he ? `שולמה במלואה (${fmt(b.owedC / 100)})` : `Paid in full (${fmt(b.owedC / 100)})`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="page" dir={he ? 'rtl' : 'ltr'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>💵 {he ? 'תשלומי לקוחות' : 'Customer Payments'}</h2>
        <span style={{ color: '#64748b', fontSize: 13 }}>
          {he ? 'כמה שולם על כל הזמנה וכמה נשאר לגבות' : 'How much of each order was paid and how much is still owed'}
        </span>
        <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-outline" style={{ color: '#15803d', borderColor: '#bbf7d0' }}
            disabled={loading || years.length === 0} onClick={exportExcel}
            title={he ? 'ייצוא הטבלה המוצגת (לפי הסינון) לאקסל' : 'Export the table shown (current filter) to Excel'}>
            📥 {he ? 'ייצוא לאקסל' : 'Export to Excel'}
          </button>
          <div className="sub-tabs" style={{ marginBottom: 0 }}>
            {[
              ['all', he ? 'הכל' : 'All'],
              ['open', he ? 'פתוחות לתשלום' : 'Open'],
              ['paid', he ? 'שולמו' : 'Paid'],
            ].map(([key, label]) => (
              <button key={key} className={`sub-tab${filter === key ? ' active' : ''}`} onClick={() => setFilter(key)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {err && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
          padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>
      )}

      {!loading && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="label">{he ? 'סה"כ הזמנות' : 'Total ordered'}</div>
            <div className="value">{fmt(all.totalC / 100)}</div>
            <div className="sub">{ordersLabel(rows.length, he)}</div>
          </div>
          <div className="summary-card success">
            <div className="label">{he ? 'התקבל' : 'Received'}</div>
            <div className="value">{fmt(all.paidC / 100)}</div>
            <div className="sub">{fmtPct(all.pctPaid)} {he ? 'מהסכום' : 'of the total'}</div>
          </div>
          <div className="summary-card warning">
            <div className="label">{he ? 'נשאר לגבות' : 'Still owed'}</div>
            <div className="value">{fmt(all.owedC / 100)}</div>
            <div className="sub">{fmtPct(all.pctDue)} {he ? 'מהסכום' : 'of the total'}</div>
          </div>
          <div className={`summary-card${overdueRows.length ? ' danger' : ''}`}>
            <div className="label">{he ? 'עבר מועד התשלום הצפוי' : 'Past expected date'}</div>
            <div className="value">{fmt(overdueC / 100)}</div>
            <div className="sub">{ordersLabel(overdueRows.length, he)}</div>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: 20 }}>{he ? 'טוען…' : 'Loading…'}</div> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr>
              <th>{he ? 'הזמנה' : 'Order'}</th>
              <th>{he ? 'תאריך' : 'Date'}</th>
              <th>{he ? 'חשבונית' : 'Invoice'}</th>
              <th>{he ? 'סטטוס' : 'Status'}</th>
              <th className="text-end">{he ? 'סכום ההזמנה' : 'Order total'}</th>
              <th className="text-end">{he ? 'שולם' : 'Paid'}</th>
              <th className="text-end">{he ? '% שולם' : '% paid'}</th>
              <th className="text-end">{he ? 'יתרה לתשלום' : 'Balance due'}</th>
              <th className="text-end">{he ? '% יתרה' : '% due'}</th>
              <th>{he ? 'תשלום צפוי' : 'Expected payment'}</th>
              <th></th>
            </tr></thead>
            <tbody>
              {years.length === 0 && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>
                  {filter === 'all'
                    ? (he ? 'אין הזמנות' : 'No orders')
                    : (he ? 'אין הזמנות בסינון הזה' : 'No orders match this filter')}
                </td></tr>
              )}
              {years.map(({ year, rows: yRows }) => {
                const s = sumBalances(yRows);
                const collapsed = !!collapsedYears[year];
                return (
                  <Fragment key={year}>
                    <tr style={{ background: '#eef2ff', cursor: 'pointer', fontWeight: 700 }}
                      onClick={() => setCollapsedYears((c) => ({ ...c, [year]: !c[year] }))}>
                      <td colSpan={4}>
                        {collapsed ? collapsedArrow : '▾ '}<bdi>{year}</bdi>
                        <bdi style={{ color: '#64748b', fontWeight: 500, marginInlineStart: 10, fontSize: 13 }}>
                          {ordersLabel(yRows.length, he)}
                        </bdi>
                      </td>
                      <td className="text-end" style={moneyCell}>{fmt(s.totalC / 100)}</td>
                      <td className="text-end" style={{ ...moneyCell, color: '#1a7a4a' }}>{fmt(s.paidC / 100)}</td>
                      <td className="text-end">{fmtPct(s.pctPaid)}</td>
                      <td className="text-end" style={{ ...moneyCell, color: '#c2410c' }}>{fmt(s.owedC / 100)}</td>
                      <td className="text-end">{fmtPct(s.pctDue)}</td>
                      <td colSpan={2}></td>
                    </tr>
                    {!collapsed && yRows.map(({ o, b }) => (
                      <Fragment key={o.id}>
                        <tr style={{ cursor: 'pointer', background: expanded === o.id ? '#f8fafc' : undefined }}
                          onClick={() => toggleOrder(o.id)}>
                          <td style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {expanded === o.id ? '▾ ' : collapsedArrow}{o.order_number}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDay(o.order_date)}</td>
                          <td style={{ fontFamily: "'DM Mono', monospace" }}>{o.invoice_number || '—'}</td>
                          <td>{statusText(o.status)}</td>
                          <td className="text-end" style={{ ...moneyCell, fontWeight: 600 }}>{fmt(b.totalC / 100)}</td>
                          <td className="text-end" style={{ ...moneyCell, color: b.paidC > 0 ? '#1a7a4a' : '#94a3b8' }}>
                            {fmt(b.paidC / 100)}
                            {o.payment_count > 1 && (
                              <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'inherit' }}>
                                {he ? `${o.payment_count} תשלומים` : `${o.payment_count} payments`}
                              </div>
                            )}
                          </td>
                          <td className="text-end" style={{ minWidth: 80 }}>
                            {fmtPct(b.pctPaid)}
                            <ProgressBar pct={b.pctPaid} done={b.settled} />
                          </td>
                          <td className="text-end" style={{ ...moneyCell, fontWeight: 600, color: b.overpaidC > 0 ? '#b91c1c' : b.open ? '#c2410c' : '#94a3b8' }}>
                            {b.overpaidC > 0 ? fmt(-b.overpaidC / 100) : fmt(b.owedC / 100)}
                          </td>
                          <td className="text-end">{fmtPct(b.pctDue)}</td>
                          <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                            {b.open ? (
                              <ExpectedDateInput order={o} overdue={b.overdue} he={he}
                                onSave={(value, message) => saveExpectedDate(o, value, message)} />
                            ) : (
                              <span style={{ color: '#94a3b8' }}>—</span>
                            )}
                          </td>
                          <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                            {b.settled ? (
                              <span style={{ color: '#1a7a4a', fontWeight: 700 }}>✓ {he ? 'שולם' : 'Paid'}</span>
                            ) : b.open && (
                              <button className="btn btn-sm btn-success" disabled={busy}
                                title={he ? 'רישום תשלום על כל היתרה, בתאריך היום' : 'Record the whole balance as paid today'}
                                onClick={() => payRemaining(o, b, today, '')}>
                                ✓ {he ? 'שולם' : 'Paid'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {expanded === o.id && renderPanel(o, b)}
                      </Fragment>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
